import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import InvalidRoute from "../components/accessDenied/InvalidRoute";
import ErrorScreen from "../errors/ErrorScreen";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import Footer from "../shared/Footer/Footer";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import "../styles/sharedStyles.scss";
import { getHeaders } from "../utils/AuthorizationUtil";
import {
  AUTHORIZE_BASE_URL,
  BASE_URL,
  INVENTORY_SERVICE_URL,
} from "../utils/EmpUtil";
import "./App.scss";
import FieldView from "./pure/FieldView";

// Pinned inputs for the prod connectivity probe. Field view users
// don't carry a dealerId, so we hit a known SA dealer to verify routing
// + CORS + auth all the way through inventory-service.
const PROBE_MARKET = "MEX";
const PROBE_DEALER_ID = "MEXM3243";

const FieldViewApp = (props) => {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] =
    useState(false);
  const [padding, setPaddingState] = useState("70px");
  const [healthChecks, setHealthChecks] = useState([]);

  const userRef = useRef(new FieldView(props.userDetails, props.history));
  const localizedInfoRef = useRef({});

  useEffect(() => {
    getLocalizedInfo();

    const unlisten = props.history.listen(() => setNavigationRestriction());

    return () => {
      unlisten();
    };
  }, []);

  // Field view is a read-only persona used to verify prod auth + CORS
  const getLocalizedInfo = () => {
    props.hideOrShow(true);
    LocalizedInfoClient.getLocalizedInfo(userRef.current)
      .then((response) => {
        localizedInfoRef.current = Object.assign(
          {},
          { ...localizedInfoRef.current },
          { ...response }
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        setNavigationRestriction();
        setRenderApp(true);
        props.hideOrShow(false);
        runProdConnectivityProbes();
      });
  };

  // Fire a small set of authenticated GETs against the same hosts a
  // real dealer/nsc session would touch. We surface the HTTP status on
  // the landing page so CORS/auth issues are visible at a glance.
  const runProdConnectivityProbes = () => {
    const probes = [
      {
        name: "Authorize",
        service: "Authorize Service",
        method: "POST",
        url: `${AUTHORIZE_BASE_URL}/authorize`,
        body: {},
      },
      {
        name: "Localized info",
        service: "Authorize Service",
        method: "GET",
        url: `${AUTHORIZE_BASE_URL}/localized-info`,
      },
      {
        name: "Translations",
        service: "Translation Service",
        method: "GET",
        url: `${AUTHORIZE_BASE_URL}/translations/es_MEX/${PROBE_MARKET}`,
      },
      {
        name: "NSC is-onboarded",
        service: "SA Service",
        method: "GET",
        url: `${BASE_URL}/nsc/${PROBE_MARKET}/is-onboarded`,
      },
      {
        name: "Inventory prices data",
        service: "Inventory Service",
        method: "GET",
        url: `${INVENTORY_SERVICE_URL}/dealer/${PROBE_DEALER_ID}/inventory-prices-data`,
      },
    ];

    setHealthChecks(
      probes.map((p) => ({ ...p, status: "pending", ok: null, error: null }))
    );

    probes.forEach((probe, idx) => {
      const headers = getHeaders();
      const request =
        probe.method === "POST"
          ? axios.post(probe.url, probe.body || {}, headers)
          : axios.get(probe.url, headers);

      request
        .then((response) => {
          setHealthChecks((prev) => {
            const next = [...prev];
            next[idx] = {
              ...probe,
              status: response.status,
              ok: response.status >= 200 && response.status < 300,
              error: null,
            };
            return next;
          });
        })
        .catch((error) => {
          const status = error?.response?.status || "ERR";
          const isCors =
            !error?.response &&
            (error?.message || "").toLowerCase().includes("network");
          setHealthChecks((prev) => {
            const next = [...prev];
            next[idx] = {
              ...probe,
              status,
              ok: false,
              error: isCors
                ? "Network/CORS error (no response)"
                : error?.message || "Request failed",
            };
            return next;
          });
        });
    });
  };

  const navigateToRoute = (link) => {
    props.history.push(`/${link}`);
  };

  const setNavigationRestriction = () => {
    const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
      props.history.location.pathname
    );
    setShouldNavigationBeRestricted(!isAllowedPath);
  };

  const setPadding = (paddingValue) => {
    if (paddingValue > 70) {
      setPaddingState(paddingValue + "px");
    }
  };

  const isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const staticAllowedPaths = ["/", "/dashboard"];

    return staticAllowedPaths.includes(currentPathName);
  };

  const statusColor = (check) => {
    if (check.ok === true) return "#1e7e34";
    if (check.ok === false) return "#c62828";
    return "#666";
  };

  const FieldViewLanding = () => (
    <div className="pageWrapper" data-testid="fieldViewLanding">
      <div style={{ padding: "32px" }}>
        <h2>Welcome, {userRef.current.fullName || userRef.current.userId}</h2>
        <p>
          You are signed in as a field view user. This view is read-only and is
          intended for verifying production access.
        </p>
        <ul>
          <li>
            <strong>User ID:</strong> {userRef.current.userId}
          </li>
          <li>
            <strong>Market:</strong> {userRef.current.market}
          </li>
          <li>
            <strong>Roles:</strong>{" "}
            {(userRef.current.roles || []).join(", ") || "—"}
          </li>
        </ul>

        <h3 style={{ marginTop: "32px" }}>Prod connectivity check</h3>
        <p style={{ color: "#555", marginTop: "-8px" }}>
          Authenticated GETs against the same hosts a normal session would
          touch. 2xx = CORS + auth are healthy on that endpoint.
        </p>
        <table
          data-testid="healthChecksTable"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            maxWidth: "960px",
          }}
        >
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
              <th style={{ padding: "8px 12px" }}>Service</th>
              <th style={{ padding: "8px 12px" }}>Endpoint</th>
              <th style={{ padding: "8px 12px" }}>URL</th>
              <th style={{ padding: "8px 12px" }}>Status</th>
              <th style={{ padding: "8px 12px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {healthChecks.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: "12px", color: "#666" }}>
                  Running checks…
                </td>
              </tr>
            )}
            {healthChecks.map((check) => (
              <tr key={check.name} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px 12px" }}>{check.service}</td>
                <td style={{ padding: "8px 12px" }}>
                  {check.method ? `${check.method} ` : ""}
                  {check.name}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    wordBreak: "break-all",
                  }}
                >
                  {check.url}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    color: statusColor(check),
                    fontWeight: 600,
                  }}
                >
                  {check.status}
                </td>
                <td style={{ padding: "8px 12px", color: "#555" }}>
                  {check.error || (check.ok ? "OK" : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    renderApp && (
      <Router history={props.history}>
        <div className="appWrapper" id="appWrapper">
          {!shouldNavigationBeRestricted && (
            <div style={{ paddingTop: padding }}>
              <GlobalNavBar
                {...props}
                user={userRef.current}
                urlPath={window.location.pathname}
                navigateToRoute={navigateToRoute}
                setPadding={setPadding}
              />
            </div>
          )}

          <Switch>
            <Route
              exact
              path="/dashboard"
              component={withIdleTimer(FieldViewLanding)}
            />

            <Route
              exact
              path="/"
              render={() => <Redirect to={{ pathname: "/dashboard" }} />}
            />

            <Route
              exact
              path="/error"
              render={(routeProps) => <ErrorScreen {...routeProps} />}
            />
            <Route component={withIdleTimer(InvalidRoute)} />
          </Switch>

          {!shouldNavigationBeRestricted && (
            <Footer permissions={props.userDetails.permissions} />
          )}
        </div>
      </Router>
    )
  );
};

export default FieldViewApp;
